import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Store } from '@ngxs/store';
import {
  ConnectionsSelectors,
  CreateBotConnection,
  FacebookPage,
  GetFacebookPages,
  RemoveBotConnection,
  ResetFacebookAuthStatus,
  UpdateBotConnection,
} from 'projects/brn-frontend/src/app/core/store/bot-connections';
import { UserState } from 'projects/brn-frontend/src/app/core/store/user/user.state';

enum ConnectionState {
  Start,
  NewOfficial,
  EditOfficial,
  NewUnofficial,
  EditUnofficial,
}

@Component({
  selector: 'brn-instagram-connection-modal',
  templateUrl: './instagram-connection-modal.component.html',
  styleUrls: ['./instagram-connection-modal.component.scss'],
})
export class InstagramConnectionModalComponent implements OnInit {
  @ViewSelectSnapshot(UserState.advancedConnectionsPermission)
  advancedConnectionsPermission!: boolean;

  @ViewSelectSnapshot(ConnectionsSelectors.pendingStatus)
  pendingStatus!: boolean;

  @ViewSelectSnapshot(ConnectionsSelectors.facebookAuthStatus)
  facebookAuthStatus!: 'not_connected' | 'pending' | 'connected';

  @ViewSelectSnapshot(ConnectionsSelectors.facebookPages)
  facebookPages!: FacebookPage[];

  states = ConnectionState;
  curentState!: ConnectionState;

  instagramOfficialForm = this.formBuilder.group({
    page: ['', Validators.required],
    official: [true],
    unofficial_fallback: [false],
    username: [''],
    password: [''],
    is_custom_proxy: [false],
    proxy: [''],
  });

  instagramUnofficialForm = this.formBuilder.group({
    username: ['', Validators.required],
    password: ['', Validators.required],
    is_custom_proxy: [true],
    proxy: ['', Validators.required],
    disallow_proxy_change: [true],
    official: [false],
    process_media: [false, Validators.required],
    feed_check_interval: [300, Validators.required],
    realtime_mode: ['web', Validators.required], // "web" | "android"
  });

  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<InstagramConnectionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.dialogRef.afterClosed().subscribe(() => {
      this.store.dispatch(new ResetFacebookAuthStatus());
    });

    this.instagramOfficialForm
      .get('unofficial_fallback')
      ?.valueChanges.subscribe((checked) => {
        this.updateValidity(checked);
      });
  }

  ngOnInit(): void {
    if (this.data.providerData && this.data.providerData.official) {
      const providerData = this.data.providerData;
      this.instagramOfficialForm.patchValue({
        page: {
          name: providerData.page_name,
          page_id: providerData.page_id,
        },
        unofficial_fallback: providerData.unofficial_fallback,
        username: providerData.username,
        password: providerData.password,
        proxy: providerData.proxy,
      });
      this.updateValidity(providerData.unofficial_fallback);
      this.curentState = this.states.EditOfficial;
    } else if (this.data.providerData && !this.data.providerData.official) {
      const providerData = this.data.providerData;
      this.instagramUnofficialForm.get('password')?.setValidators(null);
      this.instagramUnofficialForm.get('password')?.updateValueAndValidity();
      this.instagramUnofficialForm.patchValue(providerData);
      this.curentState = this.states.EditUnofficial;
    } else if (!this.data.providerData && !this.advancedConnectionsPermission) {
      this.curentState = this.states.NewOfficial;
    } else {
      this.curentState = this.states.Start;
    }
  }

  changeState(state: ConnectionState): void {
    this.curentState = state;
  }

  private updateValidity(unofficialFallback: boolean): void {
    if (unofficialFallback) {
      this.instagramOfficialForm
        .get('username')
        ?.setValidators([Validators.required]);
      this.instagramOfficialForm
        .get('password')
        ?.setValidators([Validators.required]);
      this.instagramOfficialForm
        .get('proxy')
        ?.setValidators([Validators.required]);
    } else {
      this.instagramOfficialForm.get('username')?.setValidators(null);
      this.instagramOfficialForm.get('password')?.setValidators(null);
      this.instagramOfficialForm.get('proxy')?.setValidators(null);
    }

    this.instagramOfficialForm.get('username')?.updateValueAndValidity();
    this.instagramOfficialForm.get('password')?.updateValueAndValidity();
    this.instagramOfficialForm.get('proxy')?.updateValueAndValidity();
  }

  get validOfficialForm(): boolean {
    return this.instagramOfficialForm.valid;
  }
  get validUnofficialForm(): boolean {
    return this.instagramUnofficialForm.valid;
  }
  get pageName(): string {
    return this.instagramOfficialForm.value.page.name;
  }
  get unofficialFallback(): boolean {
    return this.instagramOfficialForm.get('unofficial_fallback')?.value;
  }

  createOfficialConnection(): void {
    if (!this.validOfficialForm || this.pendingStatus) {
      return;
    }
    const form = this.instagramOfficialForm.value;

    const providerData = {
      user_token: form.page.access_token,
      page_id: form.page.id,
      official: true,
      unofficial_fallback: form.unofficial_fallback,
      username: form.username,
      password: form.password,
      proxy: form.proxy,
    };
    this.store.dispatch(new CreateBotConnection('instagram', providerData));
  }

  updateOfficialConnection(): void {
    if (!this.validOfficialForm || this.pendingStatus) {
      return;
    }

    const form = this.instagramOfficialForm.value;

    const providerData = {
      user_token: form.page.access_token,
      page_id: form.page.page_id || form.page.id,
      official: true,
      unofficial_fallback: form.unofficial_fallback,
      username: form.username,
      password: form.password,
      proxy: form.proxy,
    };
    this.store.dispatch(new UpdateBotConnection('instagram', providerData));
  }

  createUnofficialConnection(): void {
    if (!this.validUnofficialForm || this.pendingStatus) {
      return;
    }
    const providerData = this.instagramUnofficialForm.value;
    this.store.dispatch(new CreateBotConnection('instagram', providerData));
  }

  updateUnofficialConnection(): void {
    if (!this.validUnofficialForm || this.pendingStatus) {
      return;
    }
    const providerData = this.instagramUnofficialForm.value;
    this.store.dispatch(new UpdateBotConnection('instagram', providerData));
  }

  removeConnection(): void {
    if (this.pendingStatus) {
      return;
    }
    this.store.dispatch(new RemoveBotConnection('instagram'));
  }

  getFacebookIgPages(): void {
    if (
      this.facebookAuthStatus === 'not_connected' ||
      !this.facebookAuthStatus
    ) {
      this.store.dispatch(new GetFacebookPages(true));
    }
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}
